import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Reveal from '../components/Reveal';
import { PaintSpot } from '../components/HowDrawings';
import './FAQ.css';

// Questions by topic: a row of topics, and under it the questions of the one
// picked, each a native <details> (so it opens and closes with the keyboard
// and needs no state here). The answers live in the locale files, under
// faq.topics.<topic>.q.<question>. To add a question, add it there and to the
// list below; to add a topic, add its title there too.
//
// The topic is kept in the address (/faq#delivery), so it can be linked to.
// One answer is a short list rather than a paragraph: the delivery options.

const TOPICS = [
  { key: 'how', questions: ['what', 'order', 'size', 'full', 'group'] },
  { key: 'payment', questions: ['how', 'includes', 'code', 'after'] },
  { key: 'delivery', options: true, questions: ['when', 'letterbox', 'toobig', 'abroad', 'change'] },
  { key: 'painters', questions: ['account', 'join', 'more', 'deadline'] },
  { key: 'contact', questions: ['order', 'question', 'organisation'] },
];

// The ways it can be delivered, in the order they're listed (the words are
// under faq.delivery.options in the locale files).
const DELIVERY_OPTIONS = ['everything', 'split', 'pickup'];

const fromAddress = () => {
  const key = window.location.hash.slice(1);
  return TOPICS.some((topic) => topic.key === key) ? key : TOPICS[0].key;
};

function FAQ() {
  const { t } = useTranslation();
  const [active, setActive] = useState(fromAddress);
  const topic = TOPICS.find((item) => item.key === active);

  function pick(key) {
    setActive(key);
    window.history.replaceState(null, '', `#${key}`);
  }

  return (
    <div className="faq-page">
      <div className="faq h-container">
        <Reveal as="p" className="h-label">
          {t('faq.kicker')}
        </Reveal>
        <Reveal as="h1" className="h-display faq-title" delay={100}>
          {t('faq.title')}
        </Reveal>

        <Reveal className="faq-body" delay={220}>
          {/* Two very light spots of watercolour in the empty space to the
              right of the questions - see .faq-spot in FAQ.css. */}
          <PaintSpot className="faq-spot faq-spot--upper" variant="d" />
          <PaintSpot className="faq-spot faq-spot--lower" variant="e" />

          <nav className="faq-topics" aria-label={t('faq.topicsLabel')}>
            {TOPICS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`faq-topic ${item.key === active ? 'is-active' : ''}`}
                aria-current={item.key === active ? 'true' : undefined}
                onClick={() => pick(item.key)}
              >
                {t(`faq.topics.${item.key}.title`)}
              </button>
            ))}
          </nav>

          <div className="faq-list" key={active}>
            {topic.options && (
              <details className="faq-item">
                <summary>{t('faq.delivery.optionsQuestion')}</summary>
                <div className="faq-answer">
                  {DELIVERY_OPTIONS.map((option) => (
                    <p key={option}>
                      <strong>{t(`faq.delivery.options.${option}.title`)}</strong>
                      <br />
                      {t(`faq.delivery.options.${option}.text`)}
                    </p>
                  ))}
                </div>
              </details>
            )}
            {topic.questions.map((key) => (
              <details className="faq-item" key={key}>
                <summary>{t(`faq.topics.${topic.key}.q.${key}.q`)}</summary>
                <div className="faq-answer">
                  <p>{t(`faq.topics.${topic.key}.q.${key}.a`)}</p>
                </div>
              </details>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default FAQ;
